var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Renderable } from "../rendering/renderable/Renderable";
import { Mesh, PlaneGeometry, MeshBasicMaterial, CanvasTexture, Group, Texture } from "three";
import { RoomChangeType } from "../rendering/renderable/RendererModelInterface";
import { now } from "../../messages/timing";
import { globalHTMLCanvasObjects } from "../html";
import { InputEventTrigger, InteractionFacet, CollisionFacet, PrimitiveShape, UpdateFacetOnModelMessage } from "flavr-common";
import { StartSTTRecordMessage, RequestSTTMessage, STTMessage, StopSTTRecordMessage } from "../../messages/stt/STTMessages";
import { ScriptManager } from "../scripting";
import { myDomToImage } from "./myDomToImage";
import { inputEventService } from "../inputs/input/InputEventService";
import { Vector2, Vector3, Matrix4 } from "flavr-math";
import { triggerInputChange } from "../htmlCanvasCursor/HTMLUtils";
import { UpdateCollisionShapeLocalTransformMessage } from "../../messages/selection";
const PLANE_GEOMETRY = new PlaneGeometry(1, 1);
const loadedExternalPackes = new Map();
const SCRIPT_MANAGER = new ScriptManager();
const tmpWindowCoords = new Vector2();
const tmpClientCoords = new Vector2();
export class HTMLCanvasRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = HTMLCanvasRenderable.typestatic;
        this.group = new Group();
        this.invWorldMatrix = new Matrix4();
        this.tmpLocalPoint = new Vector3();
        this.lastRendering = 0;
        this.isBusy = false;
        this.scriptId = "";
        this.handleHover = (hitPoint) => {
            if (this.element === undefined)
                return;
            this.tmpLocalPoint.copy(hitPoint).applyMatrix4(this.invWorldMatrix);
            this.localPointToWindowCoords(this.element, this.tmpLocalPoint, tmpWindowCoords, tmpClientCoords);
            const selectedElement = this.getElementByCoordinates(this.element, tmpWindowCoords);
            if (selectedElement === undefined)
                return;
            const moveEvent = new MouseEvent("mousemove", {
                view: window,
                bubbles: true,
                cancelable: true,
                clientX: tmpWindowCoords.x,
                clientY: tmpWindowCoords.y
            });
            moveEvent.dispatcher = "html-gl";
            selectedElement.dispatchEvent(moveEvent);
        };
        this.onMutation = () => {
            void this.renderCanvas();
        };
        this.onSpeechElementClicked = () => {
            this.requestSpeechToText();
        };
        this.renderCanvas = () => __awaiter(this, void 0, void 0, function* () {
            if (this.element === undefined)
                return;
            const time = now();
            const delta = time - this.lastRendering;
            if (delta > 10000) {
                this.isBusy = false;
            }
            if (this.isBusy || delta < 10) {
                this.debounce(this.renderCanvas, 5);
                return;
            }
            this.lastRendering = time;
            this.isBusy = true;
            if (this.drawingCanvas && (this.drawingCanvas.clientWidth !== this.element.clientWidth || this.drawingCanvas.clientHeight !== this.element.clientHeight)) {
                this.drawingCanvas = undefined;
            }
            try {
                this.drawingCanvas = yield myDomToImage.toImage(this.element, this.drawingCanvas);
            }
            catch (error) {
                console.error("[htmlCanvasRendering]", error);
                this.isBusy = false;
                return;
            }
            if (this.drawingCanvas === undefined) {
                this.isBusy = false;
                return;
            }
            this.setCanvas(this.drawingCanvas);
            this.isBusy = false;
        });
        this.material = new MeshBasicMaterial({
            transparent: true
        });
        this.mesh = new Mesh(PLANE_GEOMETRY, this.material);
        this.mesh.matrixAutoUpdate = false;
        this.group.add(this.mesh);
        this.group.matrixAutoUpdate = false;
        this.owner.addObjectToScene(this.group);
        inputEventService.register(this.owner.modelUID, this.facetType, undefined, this.handleHover);
        this.owner.renderer.getConnection().sendMultiple([
            new UpdateFacetOnModelMessage(this.owner.modelUID, new CollisionFacet(PrimitiveShape.Box, true)),
            new UpdateCollisionShapeLocalTransformMessage(this.owner.modelUID, new Matrix4().makeScale(1, 1, 0.01).toArray()),
            new UpdateFacetOnModelMessage(this.owner.modelUID, new InteractionFacet({
                behaviors: [new InputEventTrigger(true, true)]
            }))
        ]);
    }
    localPointToWindowCoords(rootElement, localPoint, windowCoords, clientCoords) {
        const rect = rootElement.getBoundingClientRect();
        clientCoords.set((localPoint.x + 0.5) * rootElement.clientWidth, (-localPoint.y + 0.5) * rootElement.clientHeight);
        windowCoords.set(clientCoords.x + rect.left, clientCoords.y + rect.top);
    }
    getElementByCoordinates(rootElement, point) {
        var _a, _b;
        const elements = rootElement.ownerDocument.elementsFromPoint(point.x, point.y);
        for (const element of elements) {
            if (rootElement.contains(element)) {
                const w = element.ownerDocument.defaultView;
                if (w !== undefined && w !== null && element instanceof w.HTMLIFrameElement && element.contentDocument !== undefined) {
                    const iframeElements = (_b = (_a = element.contentDocument) === null || _a === void 0 ? void 0 : _a.elementsFromPoint(point.x, point.y)) !== null && _b !== void 0 ? _b : [];
                    if (iframeElements.length > 0) {
                        return iframeElements[0];
                    }
                }
                return element;
            }
        }
        return undefined;
    }
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
            this.observer = undefined;
        }
        if (this.element) {
            this.element.removeEventListener("scroll", this.renderCanvas);
            this.element.removeEventListener("resize", this.renderCanvas);
            this.element.removeEventListener("change", this.renderCanvas);
            this.element.remove();
            this.element = undefined;
        }
        if (this.speechElement) {
            this.speechElement.removeEventListener("mousedown", this.onSpeechElementClicked);
            this.speechElement = undefined;
        }
        const meshIndex = globalHTMLCanvasObjects.indexOf(this.mesh);
        if (meshIndex !== -1) {
            globalHTMLCanvasObjects.splice(meshIndex, 1);
        }
        this.owner.removeObjectFromScene(this.group);
        this.stopSpeechToText();
    }
    setVisible(visible) {
        this.group.visible = visible;
    }
    requestSpeechToText() {
        if (this.facet === undefined)
            return;
        if (!this.facet.params.speechToTextId)
            return;
        const connection = this.owner.renderer.getConnection();
        connection.send(new StartSTTRecordMessage());
        connection
            .askByMessageType(new RequestSTTMessage(true), STTMessage)
            .then((msg) => {
            if (this.facet === undefined)
                return;
            if (!this.facet.params.speechToTextId)
                return;
            const element = document.getElementById(this.facet.params.speechToTextId);
            if (element === undefined)
                return;
            const text = msg.results.length > 0 ? msg.results[0].text : "";
            triggerInputChange(element, text);
        })
            .catch(() => {
            if (this.facet === undefined)
                return;
            if (!this.facet.params.speechToTextId)
                return;
            const element = document.getElementById(this.facet.params.speechToTextId);
            if (element === undefined)
                return;
            triggerInputChange(element, "");
        });
    }
    stopSpeechToText() {
        if (this.facet === undefined)
            return;
        if (!this.facet.params.speechToTextId)
            return;
        const connection = this.owner.renderer.getConnection();
        connection.send(new StopSTTRecordMessage());
    }
    resolveExternalPackages(externalPackages) {
        return __awaiter(this, void 0, void 0, function* () {
            if (externalPackages === undefined)
                return;
            if (externalPackages.length === 0)
                return;
            const packagesPromises = [];
            for (const p of externalPackages) {
                const lp = loadedExternalPackes.get(p.link);
                if (lp) {
                    packagesPromises.push(lp);
                    continue;
                }
                const scriptEle = document.createElement("script");
                scriptEle.type = "text/javascript";
                scriptEle.src = decodeURI(p.link);
                const promise = new Promise((resolve) => {
                    scriptEle.onload = resolve;
                });
                packagesPromises.push(promise);
                loadedExternalPackes.set(p.link, promise);
                document.body.appendChild(scriptEle);
            }
            return yield Promise.all(packagesPromises);
        });
    }
    updateFacet(facet) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.resolveExternalPackages(facet.params.externalPackages);
            if (this.element === undefined) {
                this.element = document.createElement("div");
                document.body.appendChild(this.element);
            }
            if (facet.elementId) {
                this.element.id = facet.elementId;
            }
            this.element.style.position = "absolute";
            this.element.style.top = "0px";
            this.element.style.left = "0";
            this.element.style.zIndex = "-1";
            if (facet.params.height !== undefined) {
                this.element.style.height = `${facet.params.height}px`;
            }
            if (facet.params.width !== undefined) {
                this.element.style.width = `${facet.params.width}px`;
            }
            if (facet.params.scriptId) {
                if (this.scriptId !== facet.params.scriptId) {
                    this.scriptId = facet.params.scriptId;
                    const scriptConstructor = yield SCRIPT_MANAGER.getParametersScripts(this.scriptId, false);
                    if (scriptConstructor) {
                        this.script = new scriptConstructor();
                        if (this.script.onInit !== undefined) {
                            const params = {
                                element: this.element
                            };
                            if (facet.params.externalPackages) {
                                for (const p of facet.params.externalPackages) {
                                    const module = window[p.moduleName];
                                    if (module === undefined)
                                        continue;
                                    params[p.moduleName] = module;
                                }
                            }
                            try {
                                this.script.onInit(params);
                            }
                            catch (error) {
                                console.error(`Script error: ${String(error)}`);
                            }
                        }
                    }
                }
                if (this.script && this.script.onParamsChanged !== undefined) {
                    if (this.script) {
                        try {
                            this.script.onParamsChanged(facet.params.parameters);
                        }
                        catch (error) {
                            console.error(`Script error: ${String(error)}`);
                        }
                    }
                }
            }
            if (this.element === undefined)
                return;
            this.mesh.userData.element = this.element;
            if (this.observer === undefined) {
                this.observer = new MutationObserver(this.onMutation);
            }
            this.observer.observe(this.element, {
                attributes: true,
                childList: true,
                subtree: true
            });
            yield this.renderCanvas();
            requestAnimationFrame(() => {
                void this.renderCanvas();
            });
            this.element.addEventListener("scroll", this.renderCanvas, true);
            this.element.addEventListener("resize", this.renderCanvas, true);
            this.element.addEventListener("change", this.renderCanvas, true);
            if (this.speechElement) {
                this.speechElement.removeEventListener("mousedown", this.onSpeechElementClicked);
                this.speechElement = undefined;
            }
            if (facet.params.speechToTextId) {
                const element = document.getElementById(facet.params.speechToTextId);
                if (element) {
                    this.speechElement = element;
                    this.speechElement.addEventListener("mousedown", this.onSpeechElementClicked);
                }
            }
            this.updateTransform();
            this.setVisible(this.owner.isVisible());
        });
    }
    debounce(callback, wait) {
        if (this.timeout !== undefined) {
            clearTimeout(this.timeout);
        }
        this.timeout = window.setTimeout(() => {
            this.timeout = undefined;
            callback();
        }, wait);
    }
    setCanvas(canvas) {
        if (this.texture === undefined || this.texture.image !== canvas) {
            if (canvas instanceof HTMLCanvasElement) {
                const texture = new CanvasTexture(canvas);
                texture.matrixAutoUpdate = false;
                this.texture = texture;
            }
            else {
                const texture = new Texture(canvas);
                texture.matrixAutoUpdate = false;
                texture.needsUpdate = true;
                this.texture = texture;
            }
        }
        if (this.texture !== this.material.map) {
            if (this.material.map) {
                this.material.map.dispose();
            }
            this.material.map = this.texture;
        }
        this.material.needsUpdate = true;
        if (this.element) {
            this.mesh.scale.y = this.element.clientHeight / this.element.clientWidth;
        }
        this.mesh.updateMatrix();
        this.group.matrixWorldNeedsUpdate = true;
    }
    updateTransform() {
        this.group.matrix.copy(this.owner.matrixWorld);
        this.group.matrixWorldNeedsUpdate = true;
        this.invWorldMatrix.copy(this.owner.matrixWorld).invert();
    }
    onRoomChanged(type) {
        this.setVisible(type !== RoomChangeType.OUTSIDE_OF_CURRENT && this.owner.isVisible());
        if (type !== RoomChangeType.OUTSIDE_OF_CURRENT) {
            void this.renderCanvas();
            requestAnimationFrame(() => {
                void this.renderCanvas();
            });
        }
    }
}
HTMLCanvasRenderable.typestatic = "HTML2Canvas";
